// FSRS Scheduler — wraps @squeakyrobot/fsrs with autoRating integration
// Multi-cert: card state paths are cert-scoped via certPath()

import { FSRS, Rating, State } from '@squeakyrobot/fsrs';
import { certPath, read, write } from './firebase-store.js';

// Initialize FSRS with tuned parameters
const fsrs = new FSRS({
  request_retention: 0.85,    // Target 85% retention
  maximum_interval: 365,      // Cap at 1 year
  enable_fuzz: false           // Deterministic scheduling (enable in production if desired)
});

/**
 * Convert a card from Firebase (ISO strings) to FSRS format (Date objects).
 */
function cardFromFirebase(data) {
  if (!data) return null;
  return {
    due: data.due instanceof Date ? data.due : new Date(data.due),
    stability: data.stability || 0,
    difficulty: data.difficulty || 0,
    elapsed_days: data.elapsed_days || 0,
    scheduled_days: data.scheduled_days || 0,
    reps: data.reps || 0,
    lapses: data.lapses || 0,
    state: data.state ?? 0,
    last_review: data.last_review
      ? (data.last_review instanceof Date ? data.last_review : new Date(data.last_review))
      : null,
    lastGrade: data.lastGrade ?? null
  };
}

/**
 * Convert a card from FSRS format (Date objects) to Firebase-storable format (ISO strings).
 */
function cardToFirebase(card) {
  return {
    due: card.due instanceof Date ? card.due.toISOString() : card.due,
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review instanceof Date ? card.last_review.toISOString() : (card.last_review || null),
    lastGrade: card.lastGrade ?? null
  };
}

/**
 * Create a new FSRS card with default state.
 */
export function initCard() {
  return fsrs.createEmptyCard();
}

/**
 * Compute autoRating from response time — the user never self-rates.
 *
 * @param {number} responseMs - actual response time in ms
 * @param {number} expectedRtMs - expected response time for this question type/complexity
 * @param {number} difficulty - current card difficulty (unused, kept for API compat)
 * @returns {number} grade 1.0–4.0
 */
export function autoRating(responseMs, expectedRtMs, difficulty) {
  if (!expectedRtMs || expectedRtMs <= 0 || !Number.isFinite(expectedRtMs)) {
    return 2.5;
  }
  if (!Number.isFinite(responseMs) || responseMs < 0) {
    return 2.5;
  }

  const ratio = responseMs / expectedRtMs;

  let grade;
  if (ratio <= 0.5) {
    grade = 4.0;
  } else if (ratio <= 1.0) {
    grade = 4.0 - (ratio - 0.5) * 2.0;
  } else if (ratio <= 2.0) {
    grade = 3.0 - (ratio - 1.0) * 1.0;
  } else if (ratio <= 3.0) {
    grade = 2.0 - (ratio - 2.0) * 1.0;
  } else {
    grade = 1.0;
  }

  return Math.max(1.0, Math.min(4.0, grade));
}

/**
 * Grade an answer and update FSRS card state.
 * Stateless — no cert-scoping needed.
 *
 * @param {object} card - current FSRS card state
 * @param {number} responseMs - actual response time
 * @param {number} expectedRtMs - expected response time
 * @param {boolean} correct - whether the answer was correct
 * @returns {object} { newCard, grade }
 */
export function gradeAnswer(card, responseMs, expectedRtMs, correct) {
  const grade = correct
    ? autoRating(responseMs, expectedRtMs, card.difficulty)
    : 1.0;

  const fsrsCard = cardFromFirebase(card);
  const result = fsrs.scheduleWithGrade(fsrsCard, grade);
  const newCard = { ...result.card, lastGrade: grade };

  return { newCard, grade };
}

/**
 * Get card state from Firebase (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId
 */
export async function getCardState(userId, certId, questionId) {
  const raw = await read(certPath(userId, certId, 'cards/' + questionId));
  return raw ? cardFromFirebase(raw) : null;
}

/**
 * Save card state to Firebase (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId
 * @param {object} cardState
 */
export async function saveCardState(userId, certId, questionId, cardState) {
  await write(certPath(userId, certId, 'cards/' + questionId), cardToFirebase(cardState));
}

/**
 * Get all cards that are due for review (cert-scoped).
 * Sorted by most overdue first.
 * @param {string} userId
 * @param {string} certId
 */
export async function getDueCards(userId, certId) {
  const cards = await read(certPath(userId, certId, 'cards'));
  if (!cards) return [];

  const now = new Date();
  return Object.entries(cards)
    .map(([qid, raw]) => {
      const card = cardFromFirebase(raw);
      return { questionId: qid, ...card };
    })
    .filter(c => c.due <= now)
    .sort((a, b) => a.due - b.due); // Most overdue first
}

/**
 * Compute current retrievability (recall probability) for a card.
 * Stateless — no cert-scoping needed.
 */
export function getRetrievability(card) {
  if (!card.last_review && !card.lastReview) return 0;
  if (!card.stability || card.stability === 0) return 0;
  const fsrsCard = cardFromFirebase(card);
  return fsrs.getRetrievability(fsrsCard);
}

/**
 * Log recall validation data for FSRS prediction accuracy analysis (cert-scoped).
 * @param {string} userId
 * @param {string} certId
 * @param {string} questionId
 * @param {number} predictedRecall
 * @param {boolean} correct
 * @param {number} newStability
 */
export async function logRecallValidation(userId, certId, questionId, predictedRecall, correct, newStability) {
  try {
    const historyPath = certPath(userId, certId, 'cards/' + questionId + '/history');
    const existing = await read(historyPath) || [];
    existing.push({
      timestamp: Date.now(),
      predictedRecall: Math.round(predictedRecall * 1000) / 1000,
      correct,
      newStability: Math.round(newStability * 100) / 100
    });
    // Cap at 50 entries per card
    if (existing.length > 50) existing.splice(0, existing.length - 50);
    await write(historyPath, existing);
  } catch (e) {
    // Non-fatal — validation logging is observability only
  }
}

// Re-export for tests
export { State, Rating };
